/**
 * Auth provider adapter — IDL Surface Layer (/01)
 *
 * This is the SINGLE module in /01 that knows about the remote Auth
 * vendor SDK. It deliberately lives OUTSIDE /01/src so the vendor import
 * never appears inside the Surface source tree: the Frontend System
 * Directive (§7) bans the vendor SDK "in /01/src/", and /01 stays
 * vendor-neutral everywhere except this one seam. The rest of the SPA
 * imports only the vendor-neutral functions + types exported here
 * (alias: `@auth-provider`).
 *
 * Scope: AUTHENTICATION ONLY — password sign-in, session read, token
 * refresh, sign-out, and auth-state subscription. This module MUST NOT
 * expose `.from()` / `.rpc()` or any business-table access. All runtime
 * data flows through the public BFF (`/api/*`) with the access token as
 * a Bearer credential (see src/lib/idl-bridge/bff-read-client.ts).
 *
 * Config comes from build-time env (VITE_SUPABASE_URL +
 * VITE_SUPABASE_ANON_KEY, the browser-safe publishable key). When unset
 * (pure local design mode), the adapter reports "not configured" and the
 * Surface keeps a clearly-labelled local preview session instead.
 */
import {
  createClient,
  type AuthChangeEvent,
  type Session,
  type SupabaseClient,
} from '@supabase/supabase-js';

/** Vendor-neutral authenticated user projection. */
export interface AuthUser {
  id: string;
  email: string | null;
  /** Server-controlled claims (active_tenant_id, tenant_role, …). Read-only. */
  appMetadata: Record<string, unknown>;
  /** User-editable metadata (display name, …). */
  userMetadata: Record<string, unknown>;
}

/** Vendor-neutral session projection. */
export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  /** Unix seconds, or null when the provider does not report it. */
  expiresAt: number | null;
  user: AuthUser;
}

export interface AuthFailure {
  code: string;
  message: string;
  status?: number;
}

export interface AuthResult {
  session: AuthSession | null;
  error: AuthFailure | null;
}

export type AuthLifecycleEvent =
  | 'INITIAL_SESSION'
  | 'SIGNED_IN'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED'
  | 'USER_UPDATED'
  | 'PASSWORD_RECOVERY';

const AUTH_STORAGE_KEY = 'surface.auth.session.v1';

function readEnv(name: string): string | undefined {
  const value = (import.meta.env as Record<string, string | undefined>)[name];
  return value && value.length > 0 ? value : undefined;
}

const AUTH_BASE_URL = readEnv('VITE_SUPABASE_URL');
const AUTH_PUBLISHABLE_KEY = readEnv('VITE_SUPABASE_ANON_KEY');

let cachedClient: SupabaseClient | null = null;

/** True when the remote Auth provider env is configured for this build. */
export function isAuthConfigured(): boolean {
  return Boolean(AUTH_BASE_URL && AUTH_PUBLISHABLE_KEY);
}

function getClient(): SupabaseClient | null {
  if (!AUTH_BASE_URL || !AUTH_PUBLISHABLE_KEY) return null;
  if (!cachedClient) {
    cachedClient = createClient(AUTH_BASE_URL, AUTH_PUBLISHABLE_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // The SPA uses the URL hash for client-side view routing
        // (#dashboard, #settings, …). Disable auth hash detection so the
        // provider never tries to parse our view hash as an auth callback.
        detectSessionInUrl: false,
        storageKey: AUTH_STORAGE_KEY,
      },
    });
  }
  return cachedClient;
}

function toAuthUser(session: Session): AuthUser {
  const u = session.user;
  return {
    id: u.id,
    email: u.email ?? null,
    appMetadata: (u.app_metadata ?? {}) as Record<string, unknown>,
    userMetadata: (u.user_metadata ?? {}) as Record<string, unknown>,
  };
}

function toAuthSession(session: Session | null): AuthSession | null {
  if (!session) return null;
  return {
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at ?? null,
    user: toAuthUser(session),
  };
}

function toFailure(error: unknown): AuthFailure {
  const e = error as { message?: string; code?: string; status?: number } | null;
  return {
    code: e?.code ?? 'unknown',
    message: e?.message ?? 'Authentication failed.',
    status: e?.status,
  };
}

// --------------------------------------------------------------------------- //
// Magic-link redirect ingestion. The provider redirects back with the session
// in the URL hash (`#access_token=…&refresh_token=…&type=magiclink`, or
// `#error=…&error_description=…` when the one-time link is expired/used).
// `detectSessionInUrl` is intentionally false (the SPA owns its hash), so we
// capture the auth payload ONCE at module evaluation — before the router can
// rewrite the URL — and redeem it explicitly via setSession.
// --------------------------------------------------------------------------- //

interface CapturedAuthRedirect {
  accessToken?: string;
  refreshToken?: string;
  errorMessage?: string;
}

function captureAuthRedirectHash(): CapturedAuthRedirect | null {
  if (typeof window === 'undefined') return null;
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  if (!raw || (!raw.includes('access_token=') && !raw.includes('error_code=') && !raw.includes('error='))) {
    return null;
  }
  const params = new URLSearchParams(raw);
  const accessToken = params.get('access_token') ?? undefined;
  const refreshToken = params.get('refresh_token') ?? undefined;
  const errorMessage =
    params.get('error_description')?.replace(/\+/g, ' ') ?? params.get('error') ?? undefined;
  if (!accessToken && !errorMessage) return null;
  // Strip the auth payload from the address bar + history.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return { accessToken, refreshToken, errorMessage };
}

const PENDING_AUTH_REDIRECT = captureAuthRedirectHash();

/**
 * Redeem a captured magic-link redirect for a persisted session. Returns null
 * when the page was not opened from an auth redirect; returns an AuthResult
 * (session or a human-readable error such as "Email link is invalid or has
 * expired") otherwise. Idempotent per page load.
 */
export async function redeemAuthRedirect(): Promise<AuthResult | null> {
  if (!PENDING_AUTH_REDIRECT) return null;
  if (PENDING_AUTH_REDIRECT.errorMessage) {
    return { session: null, error: { code: 'otp_redirect_error', message: PENDING_AUTH_REDIRECT.errorMessage } };
  }
  const client = getClient();
  if (!client || !PENDING_AUTH_REDIRECT.accessToken || !PENDING_AUTH_REDIRECT.refreshToken) return null;
  const { data, error } = await client.auth.setSession({
    access_token: PENDING_AUTH_REDIRECT.accessToken,
    refresh_token: PENDING_AUTH_REDIRECT.refreshToken,
  });
  if (error) return { session: null, error: toFailure(error) };
  markEmailLinkEntry();
  return { session: toAuthSession(data.session), error: null };
}

// --------------------------------------------------------------------------- //
// E-mail-link entry tracking (Auth Flow v2). Operator policy: arriving via the
// magic-link ALWAYS routes through the security ceremony (set/rotate password →
// update/create passkey) before the app. Passkey logins (verifyOtp bridge) do
// NOT set this flag. UX-routing signal only — access enforcement is the
// BFF/RP `amr` gate.
// --------------------------------------------------------------------------- //

const EMAIL_LINK_ENTRY_KEY = 'surface.auth.entry.v1';

function markEmailLinkEntry(): void {
  try {
    window.sessionStorage.setItem(EMAIL_LINK_ENTRY_KEY, 'email_link');
  } catch {
    // Session storage unavailable — the server-side gates still hold.
  }
}

/** True when the current tab session began from a magic-link redirect. */
export function isEmailLinkEntry(): boolean {
  try {
    return window.sessionStorage.getItem(EMAIL_LINK_ENTRY_KEY) === 'email_link';
  } catch {
    return false;
  }
}

/** Clear after the security ceremony (rotation) completes. */
export function clearEmailLinkEntry(): void {
  try {
    window.sessionStorage.removeItem(EMAIL_LINK_ENTRY_KEY);
  } catch {
    // no-op
  }
}

/** Password sign-in for a seeded/invited account. Sign-up is intentionally not exposed (private beta). */
export async function signInWithPassword(email: string, password: string): Promise<AuthResult> {
  const client = getClient();
  if (!client) {
    return { session: null, error: { code: 'not_configured', message: 'Auth provider is not configured.' } };
  }
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) return { session: null, error: toFailure(error) };
  return { session: toAuthSession(data.session), error: null };
}

/** Read the persisted session (restored from local storage by the provider). */
export async function getCurrentSession(): Promise<AuthSession | null> {
  const client = getClient();
  if (!client) return null;
  const { data } = await client.auth.getSession();
  return toAuthSession(data.session);
}

/** Force a token refresh (e.g. after server-side tenant provisioning lands new claims). */
export async function refreshCurrentSession(): Promise<AuthResult> {
  const client = getClient();
  if (!client) {
    return { session: null, error: { code: 'not_configured', message: 'Auth provider is not configured.' } };
  }
  const { data, error } = await client.auth.refreshSession();
  if (error) return { session: null, error: toFailure(error) };
  return { session: toAuthSession(data.session), error: null };
}

export async function signOut(): Promise<void> {
  const client = getClient();
  if (!client) return;
  await client.auth.signOut();
}

/** Subscribe to auth lifecycle changes. Returns an unsubscribe function. */
export function onAuthStateChange(
  callback: (event: AuthLifecycleEvent, session: AuthSession | null) => void
): () => void {
  const client = getClient();
  if (!client) return () => undefined;
  const { data } = client.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
    callback(event as AuthLifecycleEvent, toAuthSession(session));
  });
  return () => data.subscription.unsubscribe();
}

// --------------------------------------------------------------------------- //
// Passkey (WebAuthn) relying-party calls + the magic-link session bridge.
// The relying party is a separate privileged function; this layer only forwards
// JSON (browser does the navigator.credentials ceremony) and redeems the
// one-time token for a real session via verifyOtp.
// --------------------------------------------------------------------------- //

const PASSKEY_FN = AUTH_BASE_URL ? `${AUTH_BASE_URL}/functions/v1/auth-passkey` : null;

/**
 * Call the relying-party function. `bearer` is the USER access token for
 * session-bound endpoints (register/password/security-state/admin); when
 * absent the publishable key authenticates the function gateway only
 * (challenge-authenticate/login — the anonymous ceremonies).
 */
async function callPasskey(
  subpath: string,
  body: unknown,
  bearer?: string,
  method: 'POST' | 'GET' = 'POST'
): Promise<{ ok: boolean; httpStatus: number; data: Record<string, unknown> }> {
  if (!PASSKEY_FN || !AUTH_PUBLISHABLE_KEY) return { ok: false, httpStatus: 0, data: { status: 'not_configured' } };
  // Bounded so an unreachable/slow relying party can never HANG an async route guard (REQ-001.83:
  // a hanging /security-state fetch would otherwise leave the surface on a blank pending screen).
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);
  try {
    const res = await fetch(`${PASSKEY_FN}${subpath}`, {
      method,
      headers: {
        ...(method === 'POST' ? { 'content-type': 'application/json' } : {}),
        apikey: AUTH_PUBLISHABLE_KEY,
        authorization: `Bearer ${bearer ?? AUTH_PUBLISHABLE_KEY}`,
      },
      ...(method === 'POST' ? { body: JSON.stringify(body) } : {}),
      signal: controller.signal,
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: res.ok && data.ok === true, httpStatus: res.status, data };
  } catch (error) {
    // Never surface the raw error to logs/UI; the caller maps this to a safe state (fail-open).
    return { ok: false, httpStatus: 0, data: { status: 'network_error', detail: String(error) } };
  } finally {
    clearTimeout(timeout);
  }
}

/** Structured WebAuthn challenge outcome — surfaces WHY a challenge failed so the UI can
 *  show a specific, safe state instead of a single generic "could not start" message.
 *  Carries no secret: only the HTTP status and the RP's safe status string. */
export interface WebauthnChallengeResult {
  options: Record<string, unknown> | null;
  /** HTTP status from the relying party (0 = network/unreachable, e.g. function not deployed). */
  httpStatus: number;
  /** The RP's safe status string when provided (e.g. 'fragile_session', 'not_configured'). */
  rpStatus: string | null;
}

/** Step 1: fetch a WebAuthn ceremony challenge + options from the relying party.
 *  Register ceremonies are session-bound (Auth Flow v2): pass the access token.
 *  Returns a structured result so the caller can distinguish 401/403 (session/fragile),
 *  404/5xx (challenge endpoint), and 0 (network/unreachable). */
export async function webauthnChallenge(
  ceremony: 'register' | 'authenticate',
  accessToken?: string
): Promise<WebauthnChallengeResult> {
  const { ok, httpStatus, data } = await callPasskey(
    '/challenge',
    { ceremony },
    ceremony === 'register' ? accessToken : undefined
  );
  const rpStatus = typeof data.status === 'string' ? data.status : null;
  return { options: ok ? (data.options as Record<string, unknown>) : null, httpStatus, rpStatus };
}

/** Step 2 (register): store the newly created passkey for the SESSION user. */
export async function webauthnRegister(accessToken: string, attestationResponse: unknown): Promise<boolean> {
  const { ok } = await callPasskey('/register', { attestationResponse }, accessToken);
  return ok;
}

// --------------------------------------------------------------------------- //
// Security-setup endpoints (Auth Flow v2). All session-bound; the RP enforces
// that fragile (password-only) sessions can never use them.
// --------------------------------------------------------------------------- //

export interface SecurityState {
  passwordSet: boolean;
  passkeyCount: number;
  setupComplete: boolean;
  fragile: boolean;
}

/** Set or rotate the account password (e-mail-proven sessions only). */
export async function securitySetPassword(
  password: string,
  accessToken: string
): Promise<{ ok: boolean; status: string }> {
  const { ok, data } = await callPasskey('/password', { password }, accessToken);
  return { ok, status: String(data.status ?? (ok ? 'password_set' : 'error')) };
}

/** Server-derived security setup state (router gate input). */
export async function fetchSecurityState(accessToken: string): Promise<SecurityState | null> {
  const { ok, data } = await callPasskey('/security-state', null, accessToken, 'GET');
  if (!ok) return null;
  return {
    passwordSet: Boolean(data.password_set),
    passkeyCount: Number(data.passkey_count ?? 0),
    setupComplete: Boolean(data.setup_complete),
    fragile: Boolean(data.fragile),
  };
}

export interface AdminInvitePayload {
  email: string;
  role: 'owner' | 'admin' | 'developer' | 'user' | 'viewer';
  tenantId?: string;
  newTenant?: { slug: string; parentTenantId: string };
}

export interface TenantNode {
  id: string;
  parent_tenant_id: string | null;
  depth: number;
  slug: string;
  status: string;
}

/** Invite a user into the tenant tree (owner/admin sessions; PBAC at the RP). */
export async function adminInvite(
  payload: AdminInvitePayload,
  accessToken: string
): Promise<{ ok: boolean; status: string; tenant?: { id: string; slug: string; depth: number } }> {
  const body: Record<string, unknown> = { email: payload.email, role: payload.role };
  if (payload.newTenant) {
    body.new_tenant = { slug: payload.newTenant.slug, parent_tenant_id: payload.newTenant.parentTenantId };
  } else if (payload.tenantId) {
    body.tenant_id = payload.tenantId;
  }
  const { ok, data } = await callPasskey('/admin/invite', body, accessToken);
  return {
    ok,
    status: String(data.status ?? (ok ? 'invited' : 'error')),
    tenant: data.tenant as { id: string; slug: string; depth: number } | undefined,
  };
}

/** The caller's tenant subtree (Invite panel data). */
export async function adminTenants(accessToken: string): Promise<TenantNode[] | null> {
  const { ok, data } = await callPasskey('/admin/tenants', null, accessToken, 'GET');
  return ok ? ((data.tenants as TenantNode[]) ?? []) : null;
}

/** Step 2 (login): verify the assertion; returns the one-time token to mint a session. */
export async function webauthnLogin(
  attestationResponse: unknown
): Promise<{ email: string; tokenHash: string } | null> {
  const { ok, data } = await callPasskey('/login', { attestationResponse });
  if (!ok || typeof data.token_hash !== 'string' || typeof data.email !== 'string') return null;
  return { email: data.email, tokenHash: data.token_hash };
}

/** Redeem the one-time token for a real session (the passkey session bridge). */
export async function verifyEmailOtp(tokenHash: string): Promise<AuthResult> {
  const client = getClient();
  if (!client) return { session: null, error: { code: 'not_configured', message: 'Auth provider is not configured.' } };
  // A magic-link token_hash verifies as type 'email' in current Supabase; fall back to the
  // legacy 'magiclink' type for older projects (the session-mint bridge — spec §5).
  const first = await client.auth.verifyOtp({ type: 'email', token_hash: tokenHash });
  const outcome = first.error
    ? await client.auth.verifyOtp({ type: 'magiclink', token_hash: tokenHash })
    : first;
  if (outcome.error) return { session: null, error: toFailure(outcome.error) };
  return { session: toAuthSession(outcome.data.session), error: null };
}

/** Send a magic-link (enrollment + fallback). Does not create users (private beta). */
export async function sendMagicLink(email: string): Promise<AuthResult> {
  const client = getClient();
  if (!client) return { session: null, error: { code: 'not_configured', message: 'Auth provider is not configured.' } };
  const { error } = await client.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  if (error) return { session: null, error: toFailure(error) };
  return { session: null, error: null };
}
